import React, { useEffect, useMemo } from "react";

import { useSelector } from "react-redux";

import { Link } from "react-router-dom";

import { createSelector } from "@reduxjs/toolkit";

import ArticleBox from "./ArticleBox";

import { formatDate } from "../../utils/helps";

import ArticleBoxByCategory from "./ArticleBoxByCategory";

import LinkButtonTitle from "../LinkButtonTitle";

import MemberProducts from "./MemberProducts";

import ActivityBox from "./ActivityBox";

import { getExcerpt } from "../../utils/html";

import {
  DesktopAdvertisementReplaceSlot,
  hasAdvertisementByPosition,
} from "./AdvertisementDisplay";

const API_URL = process.env.REACT_APP_API_URL || "";

const DESKTOP_MAIN_AD_EXTRA_WIDTH_PX = 80;

const DESKTOP_MAIN_AD_MIN_HEIGHT = 760;

const ABOUT_POST_SLUGS = [
  "qua-trinh-hinh-thanh-va-phat-trien",
  "thu-ngo-cua-chu-tich-hoi",
  "co-cau-to-chuc",
  "dieu-le-hoi",
  "quy-che-hoat-dong-cua-hoi",
  "quy-che-hoat-dong-ban-chap-hanh",
  "quy-che-chi-tieu-noi-bo",
];

// -------------------- Helpers --------------------

const selectRawPosts = (state) => state.posts?.posts || [];

const commonFilter = (post) =>
  post?.type !== "notification" &&
  post?.status === "published" &&
  !ABOUT_POST_SLUGS.includes(post?.slug) &&
  post.category?.slug !== "chinh-sach" &&
  post?.slug !== "dieu-khoan";

const groupByCategory = (posts) =>
  posts.reduce((acc, post) => {
    const categoryName = post?.category?.name || "Khác";

    if (!acc[categoryName]) acc[categoryName] = [];

    acc[categoryName].push(post);

    return acc;
  }, {});

// -------------------- Memoized selectors --------------------

const selectMainPosts = createSelector([selectRawPosts], (posts) =>
  posts.filter(
    (p) => commonFilter(p) && p?.category?.slug !== "hoat-dong-khac",
  ),
);

const selectOtherActivitiesPosts = createSelector([selectRawPosts], (posts) =>
  posts.filter(
    (p) => commonFilter(p) && p?.category?.slug === "hoat-dong-khac",
  ),
);

const selectGroupedMain = createSelector([selectMainPosts], groupByCategory);

const selectGroupedOther = createSelector(
  [selectOtherActivitiesPosts],
  groupByCategory,
);

const selectNotificationPost = (state) =>
  state?.posts?.posts?.filter((p) => p?.type === "notification");

const getMainAdGridStyle = () => ({
  gridTemplateColumns: `minmax(0, calc(75% - ${DESKTOP_MAIN_AD_EXTRA_WIDTH_PX}px)) minmax(0, calc(25% + ${DESKTOP_MAIN_AD_EXTRA_WIDTH_PX}px))`,
});

const renderGridItemsWithAds = ({
  items = [],
  leftPositionKey,
  rightPositionKey,
  adCategories = [],
  adMedias = [],
  renderItem,
  maxItems = 9,
  hideRightAd = false,
}) => {
  const hasLeftAd = hasAdvertisementByPosition({
    positionKey: leftPositionKey,

    categories: adCategories,

    medias: adMedias,
  });

  const hasRightAd =
    !hideRightAd &&
    hasAdvertisementByPosition({
      positionKey: rightPositionKey,

      categories: adCategories,

      medias: adMedias,
    });

  const totalAdCount = Number(hasLeftAd) + Number(hasRightAd);

  const visibleItems = items.slice(0, Math.max(maxItems - totalAdCount, 0));

  const result = [];

  if (hasLeftAd) {
    result.push(
      <DesktopAdvertisementReplaceSlot
        key={leftPositionKey}
        positionKey={leftPositionKey}
        categories={adCategories}
        medias={adMedias}
      />,
    );
  }

  visibleItems.forEach((item) => {
    result.push(renderItem(item));
  });

  if (hasRightAd) {
    result.push(
      <DesktopAdvertisementReplaceSlot
        key={rightPositionKey}
        positionKey={rightPositionKey}
        categories={adCategories}
        medias={adMedias}
      />,
    );
  }

  return result;
};

// ------------------------------ Component --------------------------------------

export default function NewsLayout({ data, adCategories = [], adMedias = [] }) {
  const notificationPost = useSelector(selectNotificationPost);

  const posts = useSelector(selectMainPosts);

  const groupedPosts = useSelector(selectGroupedMain);

  const groupedOtherActivitiesPosts = useSelector(selectGroupedOther);

  const heroPost = posts[0];

  const secondaryPosts = posts.slice(1, 5);

  const hasMainRightAd = hasAdvertisementByPosition({
    positionKey: "desktop_replace_posts_right",

    categories: adCategories,

    medias: adMedias,
  });

  const { upcomingActs, pastActs } = useMemo(() => {
    const raw = Array.isArray(notificationPost)
      ? notificationPost
      : notificationPost?.data || [];

    const items = raw.filter((p) => p?.status === "published");

    const now = new Date();

    const upcoming = items

      .filter((i) => i?.eventStartTime && new Date(i.eventStartTime) >= now)

      .sort((a, b) => new Date(a.eventStartTime) - new Date(b.eventStartTime));

    const past = items

      .filter((i) => i?.eventStartTime && new Date(i.eventStartTime) < now)

      .sort((a, b) => new Date(b.eventStartTime) - new Date(a.eventStartTime));

    return { upcomingActs: upcoming, pastActs: past };
  }, [notificationPost]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const renderSecondaryPosts = ({ hideRightAd = false } = {}) => {
    const gridItems = renderGridItemsWithAds({
      items: secondaryPosts,

      leftPositionKey: "desktop_replace_posts_left",

      rightPositionKey: "desktop_replace_posts_right",

      adCategories,

      adMedias,

      renderItem: (article) => <ArticleBox key={article.id} article={article} />,

      maxItems: 4,

      hideRightAd,
    });

    if (gridItems.length <= 0) {
      return null;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 auto-rows-fr items-stretch gap-8 mb-12">
        {gridItems}
      </div>
    );
  };

  const renderSecondaryPostsInLeftColumn = () => {
    const gridItems = renderGridItemsWithAds({
      items: secondaryPosts,

      leftPositionKey: "desktop_replace_posts_left",

      rightPositionKey: "desktop_replace_posts_right",

      adCategories,

      adMedias,

      renderItem: (article) => <ArticleBox key={article.id} article={article} />,

      maxItems: 3,

      hideRightAd: true,
    });

    if (gridItems.length <= 0) {
      return null;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 auto-rows-fr items-stretch gap-8 mb-12">
        {gridItems}
      </div>
    );
  };

  const renderActivityGridWithAds = (articles, sliceCount, options = {}) => {
    const gridItems = renderGridItemsWithAds({
      items: articles,

      leftPositionKey: "desktop_replace_events_left",

      rightPositionKey: "desktop_replace_events_right",

      adCategories,

      adMedias,

      renderItem: (article) => <ActivityBox key={article.id} article={article} />,

      maxItems: sliceCount,

      hideRightAd: options.hideRightAd,
    });

    return (
      <div
        className={
          options.compact
            ? "grid grid-cols-1 md:grid-cols-3 auto-rows-fr items-stretch gap-6"
            : "grid grid-cols-1 md:grid-cols-3 auto-rows-fr items-stretch gap-6 px-4"
        }
      >
        {gridItems}
      </div>
    );
  };

  const renderCategoryGridWithAds = (articles, options = {}) => {
    const gridItems = renderGridItemsWithAds({
      items: articles,

      leftPositionKey: "desktop_replace_news_left",

      rightPositionKey: "desktop_replace_news_right",

      adCategories,

      adMedias,

      renderItem: (article) => (
        <ArticleBoxByCategory key={article.id} article={article} />
      ),

      maxItems: options.compact ? 6 : 9,

      hideRightAd: options.hideRightAd,
    });

    return (
      <div
        className={
          options.compact
            ? "grid grid-cols-1 md:grid-cols-3 auto-rows-fr items-stretch gap-6"
            : "grid grid-cols-1 md:grid-cols-3 auto-rows-fr items-stretch gap-6 px-4"
        }
      >
        {gridItems}
      </div>
    );
  };

  const renderUpcomingSection = (options = {}) => {
    if (upcomingActs.length <= 0) {
      return null;
    }

    return (
      <div className="mb-12">
        <LinkButtonTitle title="Hoạt động / Sự kiện sắp diễn ra">
          <Link
            to="/thong-bao"
            className="font-semibold text-blue-800 hover:text-blue-600 transition duration-200"
          >
            Xem tất cả
          </Link>
        </LinkButtonTitle>

        <div
          className={
            options.compact
              ? "text-sm text-gray-600 mb-3"
              : "px-4 text-sm text-gray-600 mb-3"
          }
        >
          Sắp diễn ra
        </div>

        <div className="mb-8">
          {renderActivityGridWithAds(upcomingActs, options.compact ? 6 : 9, {
            compact: options.compact,

            hideRightAd: options.hideRightAd,
          })}
        </div>
      </div>
    );
  };

  const renderCategorySection = (category, articles, keyPrefix, options = {}) => (
    <div key={`${keyPrefix}-${category}`} className="mb-12">
      <LinkButtonTitle title={category}>
        <Link
          to={`/hoat-dong/${articles[0]?.category?.slug}`}
          className="font-semibold text-blue-800 hover:text-blue-600 transition duration-200"
        >
          Xem tất cả
        </Link>
      </LinkButtonTitle>

      {renderCategoryGridWithAds(articles, {
        compact: options.compact,

        hideRightAd: options.hideRightAd,
      })}
    </div>
  );

  const renderPastSection = (options = {}) => {
    if (pastActs.length <= 0) {
      return null;
    }

    return (
      <div className="mb-12">
        <LinkButtonTitle title="Hoạt động / Sự kiện gần đây">
          <Link
            to="/thong-bao"
            className="font-semibold text-blue-800 hover:text-blue-600 transition duration-200"
          >
            Xem tất cả
          </Link>
        </LinkButtonTitle>

        <div
          className={
            options.compact
              ? "text-sm text-gray-600 mb-3"
              : "px-4 text-sm text-gray-600 mb-3"
          }
        >
          Đã diễn ra gần đây
        </div>

        {renderActivityGridWithAds(
          pastActs,

          upcomingActs.length > 0 ? 6 : 9,

          {
            compact: options.compact,

            hideRightAd: options.hideRightAd,
          },
        )}
      </div>
    );
  };

  const groupedMainEntries = Object.entries(groupedPosts);

  const groupedOtherEntries = Object.entries(groupedOtherActivitiesPosts);

  const firstCategoryEntry = groupedMainEntries[0];

  const firstOtherEntry = !firstCategoryEntry ? groupedOtherEntries[0] : null;

  const hasNextSectionForMainAd =
    upcomingActs.length > 0 ||
    !!firstCategoryEntry ||
    !!firstOtherEntry ||
    pastActs.length > 0 ||
    (data || []).length > 0;

  const renderFirstSectionBesideAd = () => {
    if (upcomingActs.length > 0) {
      return renderUpcomingSection({ compact: true, hideRightAd: true });
    }

    if (firstCategoryEntry) {
      return renderCategorySection(
        firstCategoryEntry[0],

        firstCategoryEntry[1],

        "category",

        {
          compact: true,

          hideRightAd: true,
        },
      );
    }

    if (firstOtherEntry) {
      return renderCategorySection(
        firstOtherEntry[0],

        firstOtherEntry[1],

        "other-activities",

        {
          compact: true,

          hideRightAd: true,
        },
      );
    }

    if (pastActs.length > 0) {
      return renderPastSection({ compact: true, hideRightAd: true });
    }

    return (
      <MemberProducts
        data={data}
        adCategories={adCategories}
        adMedias={adMedias}
        compact={true}
        hideRightAd={true}
      />
    );
  };

  const shouldRenderUpcomingAfterMainAd = !(
    hasMainRightAd && upcomingActs.length > 0
  );

  const shouldRenderFirstCategoryAfterMainAd = !(
    hasMainRightAd &&
    upcomingActs.length <= 0 &&
    firstCategoryEntry
  );

  const shouldRenderFirstOtherAfterMainAd = !(
    hasMainRightAd &&
    upcomingActs.length <= 0 &&
    !firstCategoryEntry &&
    firstOtherEntry
  );

  const shouldRenderPastAfterMainAd = !(
    hasMainRightAd &&
    upcomingActs.length <= 0 &&
    !firstCategoryEntry &&
    !firstOtherEntry &&
    pastActs.length > 0
  );

  const shouldRenderProductsAfterMainAd = !(
    hasMainRightAd &&
    upcomingActs.length <= 0 &&
    !firstCategoryEntry &&
    !firstOtherEntry &&
    pastActs.length <= 0
  );

  return (
    <div className="container mx-auto py-2 mb-10">
      {/* Bài chính đầu trang */}

      {heroPost && (
        <div className="flex flex-col lg:flex-row gap-8 mb-12">
          <div className="lg:w-2/3">
            <div className="w-full aspect-[4/3] sm:aspect-[16/10] lg:aspect-[16/9] bg-gray-100 shadow-md overflow-hidden">
              <img
                src={
                  heroPost?.images?.[0]?.url
                    ? `${API_URL}media/${heroPost.images[0].url}`
                    : undefined
                }
                alt={heroPost?.title || ""}
                className="w-full h-full object-fill"
              />
            </div>
          </div>

          <div className="lg:w-1/3 flex flex-col justify-start">
            <Link to={`/post/${heroPost.slug}`}>
              <h1 className="text-3xl font-bold text-justify hover:text-blue-600 transition">
                {heroPost.title}
              </h1>
            </Link>

            <p className="text-sm text-gray-500 mt-4">
              {formatDate(heroPost.createdAt)}
            </p>

            <p className="text-base text-gray-700 mt-4 leading-relaxed text-justify">
              {getExcerpt(heroPost.content)}...
            </p>
          </div>
        </div>
      )}

      {hasMainRightAd && hasNextSectionForMainAd ? (
        <>
          <div className="lg:hidden">
            {renderSecondaryPosts({ hideRightAd: true })}

            {renderFirstSectionBesideAd()}
          </div>

          <div
            className="hidden lg:grid gap-8 mb-12 items-start"
            style={getMainAdGridStyle()}
          >
            <div>
              {renderSecondaryPostsInLeftColumn()}

              {renderFirstSectionBesideAd()}
            </div>

            <div className="h-full">
              <DesktopAdvertisementReplaceSlot
                positionKey="desktop_replace_posts_right"
                categories={adCategories}
                medias={adMedias}
                className={`h-full min-h-[${DESKTOP_MAIN_AD_MIN_HEIGHT}px]`}
              />
            </div>
          </div>
        </>
      ) : (
        renderSecondaryPosts()
      )}

      {/* Hoạt động / Sự kiện sắp diễn ra */}

      {shouldRenderUpcomingAfterMainAd && renderUpcomingSection()}

      {/* Bài theo danh mục */}

      {groupedMainEntries.map(([category, articles], index) => {
        if (!shouldRenderFirstCategoryAfterMainAd && index === 0) {
          return null;
        }

        if (hasMainRightAd && !shouldRenderUpcomingAfterMainAd && index === 0) {
          return null;
        }

        return renderCategorySection(category, articles, "category");
      })}

      {/* Bài thuộc "hoạt động khác" */}

      {groupedOtherEntries.map(([category, articles], index) => {
        if (!shouldRenderFirstOtherAfterMainAd && index === 0) {
          return null;
        }

        return renderCategorySection(category, articles, "other-activities");
      })}

      {/* Hoạt động / Sự kiện từ notificationPost */}

      {shouldRenderPastAfterMainAd && renderPastSection()}

      {/* Sản phẩm hội viên */}

      {shouldRenderProductsAfterMainAd && (
        <MemberProducts
          data={data}
          adCategories={adCategories}
          adMedias={adMedias}
        />
      )}
    </div>
  );
}